/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useExportStatement
 */
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import api from 'client/index';
import AppEvents, { Events, Messages } from '@veritable/common/utils/appEvents';
import { downloadBlobFile } from '@veritable/common/utils';
import { MONTH_DATE_YEAR_TIME_FORMAT } from '@veritable/common/constants';

/**
 * Mutate to export statement
 */

interface IProps {
	statementGenerationRequestModel: StatementGenerationRequestModel;
	entityName: string;
}

const useExportStatement = (): UseMutationResult<unknown, IAxiosError, IProps, unknown> => {
	return useMutation({
		mutationFn: async ({ statementGenerationRequestModel, entityName }) => {
			const { data }: AnyType = await api.StatementGenerationApi.generateStatement(statementGenerationRequestModel, {
				responseType: 'blob',
			});

			const mimeType = statementGenerationRequestModel.statementRequestModel?.splitStatement ? 'application/zip' : 'application/pdf';
			downloadBlobFile(
				data,
				_.kebabCase(`${entityName}-statement-${dateParser().format(MONTH_DATE_YEAR_TIME_FORMAT)}`),
				mimeType,
				'download',
			);
		},
		onSuccess: () => {
			AppEvents.publishApiSuccess();
		},
		onMutate: () => {
			AppEvents.publish(Events.Loading, Messages.DownloadingFile);
		},
		onError: (error) => {
			const fileReader = new FileReader();
			fileReader.addEventListener('loadend', () => {
				const parsedError = { response: { data: JSON.parse(fileReader.result as string) } } as IAxiosError;
				AppEvents.publishApiError({ error: parsedError });
			});
			fileReader.readAsText(error.response?.data as unknown as AnyType);
		},
	});
};
export default useExportStatement;
